import CommonPage from '@/components/CommonPage';
import useMenu from '@/hooks/useMenu';
import { useIntl } from '@/i18n';
import { Descriptions, Empty, Tag, Typography } from 'antd';
import { useLocation } from 'react-router';

interface PagePlaceholderProps {
  /** 页面权限码（PRD 路由键，或 menuCatalog 常显项的合成键），同时用来取页面名（三语） */
  code: string;
  /** 对应高保真原型 NAV_ADMIN 里的 key，方便回原型对照 PRD */
  prd?: string;
  /**
   * 这一页为什么还没内容，决定文案与是否列接口清单：
   *
   * - `'ui'`（默认）——**页面还没写**。一期总台 56 个二级页面先跑通路由 / 菜单 /
   *   权限码 / 面包屑，业务实现按模块逐个替换掉这个占位。
   * - `'api'`——**页面已经写完了，在等后端接口**。mock 退役后
   *   （`docs/superpowers/specs/2026-09-02-mock-retirement-design.md`），这类页面的
   *   路由指向本组件而不是页面文件本身：不被 import 就不发请求，也就不会报错。
   *   页面文件原封不动留在原地，接上后端时把 `router/index.tsx` 里的 `element`
   *   改回 `lazy` 即可。
   *
   * 两者必须分开说：一个是「还没做」，一个是「做完了在等别人」，对读到这一页的人
   * （尤其是后端同事）意味着完全不同的下一步。
   */
  pending?: 'ui' | 'api';
  /**
   * `pending='api'` 时本页在等哪些接口，直接写 mock 时代的路径即可。
   *
   * 这一栏是给后端同事看的——它同时也是「待接后端」清单的可执行形式：
   * `src/services/` 里那些指向 `/muou/*` 的函数没有运行时调用方了，这里列出来
   * 的路径就是它们的去向。
   */
  apis?: string[];
}

/**
 * 页面骨架占位
 *
 * 两种形态见 {@link PagePlaceholderProps.pending}。无论哪种，都遵守
 * `docs/superpowers/plans/2026-08-19-组织会务前端.md` 立的三条禁令：
 * ❌ 静默返回空　❌ 显示成 0　❌ 把入口藏起来——菜单入口照常在，点进来写明原因。
 */
export default function PagePlaceholder({
  code,
  prd,
  pending = 'ui',
  apis,
}: PagePlaceholderProps) {
  const { getTitle, titleOf, breadcrumbs } = useMenu();
  const location = useLocation();
  const intl = useIntl();

  // 侧边栏里没有这一项（服务端没下发这颗键、或本来就没有键）也照样取得到名字
  const title = titleOf(code);

  return (
    <CommonPage title={title}>
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          <span className="text-secondary">
            {intl.formatMessage({
              id:
                pending === 'api'
                  ? 'placeholder.pendingApi'
                  : 'placeholder.pendingUi',
            })}
          </span>
        }
      />
      <Descriptions
        size="small"
        column={1}
        bordered
        className="mx-auto max-w-xl"
        items={[
          {
            key: 'menu',
            label: intl.formatMessage({ id: 'placeholder.menuPath' }),
            children: breadcrumbs.map((item) => getTitle(item)).join(' / '),
          },
          {
            key: 'path',
            label: intl.formatMessage({ id: 'placeholder.route' }),
            children: <code>{location.pathname}</code>,
          },
          {
            key: 'code',
            label: intl.formatMessage({ id: 'placeholder.code' }),
            children: <Tag>{code}</Tag>,
          },
          {
            key: 'prd',
            label: intl.formatMessage({ id: 'placeholder.prd' }),
            children: prd ? <code>NAV_ADMIN · {prd}</code> : '—',
          },
          // 只有「等接口」这一形态才列，且必须真的传了清单才铺这一行——
          // 空数组铺一行空白比不铺更让人以为是渲染坏了
          ...(pending === 'api' && apis?.length
            ? [
                {
                  key: 'apis',
                  label: intl.formatMessage({ id: 'placeholder.apis' }),
                  children: (
                    <Typography.Paragraph className="mb-0!">
                      {apis.map((api) => (
                        <div key={api}>
                          <code>{api}</code>
                        </div>
                      ))}
                    </Typography.Paragraph>
                  ),
                },
              ]
            : []),
        ]}
      />
    </CommonPage>
  );
}
